import * as v from 'valibot';
import { SequenceTypeSchema } from '../types/sequencingTypeSchema';

export const orderingSchema = v.object({
  summaryTable: v.array(v.pipe(v.string(), v.uuid())),
  productCards: v.array(v.pipe(v.string(), v.uuid())),
});

const mlOrderingSchema = v.object({
  desktop: orderingSchema,
  mobile: orderingSchema,
});

export const cloudfrontUpdatedDateSchema = v.object({
  updatedDate: v.pipe(v.string(), v.isoTimestamp()),
  // TODO: make this required once Cloudfront has the version field set.
  version: v.optional(v.number(), -1),
});

export const asyncSequencingResponseSchema = v.variant('ok', [
  v.object({ ok: v.literal(true), s: SequenceTypeSchema, message: v.optional(v.string()) }),
  v.object({ ok: v.literal(false), message: v.optional(v.string()) }),
]);

export const cloudFrontSectionSpecificSchema = v.objectWithRest(
  cloudfrontUpdatedDateSchema.entries,
  mlOrderingSchema,
);

const uuidSchema = v.pipe(v.string(), v.uuid());
const slugSchema = v.pipe(
  v.string(),
  v.minLength(3, 'Slug must be at least 3 characters'),
  v.custom(str => {
    const result = v.safeParse(uuidSchema, str);
    return !result.success;
  }, 'the slug should not be a UUID'),
);

const overrideDetailSchema = v.union([
  v.object({
    type: v.literal('fixed'),
    position: v.number(),
  }),
  v.object({
    type: v.literal('random-range'),
    range: v.pipe(
      v.string(),
      v.regex(/\d-\d/),
      v.transform(str => {
        const [min, max] = str.split('-').map(value => Number(value));
        if (min < 0) {
          throw new Error('Invalid range. min value should always be higher than 0.');
        }
        if (min >= max) {
          throw new Error('Invalid range. max value should always be higher than min.');
        }
        return str;
      }),
    ),
  }),
  v.pipe(
    v.object({
      type: v.literal('bounded-range'),
      bottomRank: v.undefinedable(v.pipe(v.number(), v.minValue(1))),
      topRank: v.undefinedable(v.pipe(v.number(), v.minValue(1))),
    }),
    v.transform(obj => {
      const { bottomRank, topRank } = obj;
      if (bottomRank === undefined && topRank === undefined) {
        throw new Error('Bounded range rules must specify a "bottomRank" and/or a "topRank".');
      }
      if (bottomRank !== undefined && topRank !== undefined && topRank >= bottomRank) {
        throw new Error('Invalid range. "topRank" value should always be lower than "bottomRank".');
      }
      return obj;
    }),
  ),
]);

export const overridesSchema = v.record(
  uuidSchema,
  v.union([
    v.object({
      desktop: v.optional(overrideDetailSchema),
      mobile: overrideDetailSchema,
    }),
    v.object({
      desktop: overrideDetailSchema,
      mobile: v.optional(overrideDetailSchema),
    }),
    v.object({
      allDevices: overrideDetailSchema,
    }),
  ]),
);

export const positionOverridesSchema = v.record(slugSchema, overridesSchema);

export const arpiSequencingSchema = v.record(
  v.pipe(v.string(), v.trim(), v.minLength(3, 'Slug should be at least 3 characters')),
  v.object({
    desktop: v.array(v.pipe(v.string(), v.uuid()), 'desktop is required for ordering'),
    mobile: v.array(v.pipe(v.string(), v.uuid()), 'mobile is required for ordering'),
  }),
);

export const cloudFrontPositionOverridesSchema = v.pipe(
  v.record(v.string(), v.any()),
  v.custom((input: any) => {
    if (!('updatedDate' in input)) {
      return false;
    }

    const { updatedDate, version, ...otherInput } = input;
    const cloudfrontUpdatedDateResult = v.safeParse(cloudfrontUpdatedDateSchema, {
      updatedDate,
      version,
    });
    const positionOverridesSchemaResult = v.safeParse(positionOverridesSchema, { ...otherInput });

    return cloudfrontUpdatedDateResult.success && positionOverridesSchemaResult.success;
  }, 'Expected updatedDate and slugs as keys'),
);

export const cloudFrontArpiSequencingSchema = v.pipe(
  v.record(v.string(), v.any()),
  v.custom((input: any) => {
    if (!('updatedDate' in input)) {
      return false;
    }

    const { updatedDate, version, ...otherInput } = input;
    const cloudfrontUpdatedDateResult = v.safeParse(cloudfrontUpdatedDateSchema, {
      updatedDate,
      version,
    });
    const arpiSequencingSchemaResult = v.safeParse(arpiSequencingSchema, { ...otherInput });

    return cloudfrontUpdatedDateResult.success && arpiSequencingSchemaResult.success;
  }, 'Expected updatedDate and slugs as keys'),
);
